import { Link } from '@remix-run/react'
import { ArrowRight, MapPin, TreeDeciduous } from 'lucide-react'
import { AppNavigation } from './app-navigation'

export function GiftLanding({
	occasion,
}: {
	occasion: 'anniversary' | 'where-we-met'
}) {
	const anniversary = occasion === 'anniversary'
	return (
		<div className="min-h-screen bg-[#f7f5ee] pb-24 text-[#243e35] md:pb-0 md:pl-[76px]">
			<AppNavigation />
			<header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-7">
				<Link to="/?map=1&select=paid" className="text-2xl font-black">
					erda.
				</Link>
				<nav aria-label="Main navigation" className="flex gap-5 text-sm">
					<Link to="/digital-gift">How gifting works</Link>
					<Link to="/info">About & FAQs</Link>
				</nav>
			</header>
			<main className="mx-auto max-w-6xl px-6 pb-20">
				<section className="grid items-center gap-12 py-12 md:grid-cols-2 md:py-24">
					<div>
						<p className="mb-5 text-xs font-bold uppercase tracking-widest">
							A meaningful place. A personal gift.
						</p>
						<h1 className="text-4xl font-black leading-tight tracking-tight md:text-6xl">
							{anniversary
								? 'A digital anniversary gift with a place in your story.'
								: 'Give the place where you first met.'}
						</h1>
						<p className="mt-6 max-w-lg text-lg leading-relaxed">
							{anniversary
								? 'Your first date. The place you said yes. Your favourite corner of the world. Mark another year together with a personal digital place that helps fund real trees.'
								: 'That little café, a university courtyard, the park where you first said hello. Choose a digital place near the place where it all began and make it part of your gift.'}
						</p>
						<Link
							to="/?map=1&select=paid"
							className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#243e35] px-7 py-4 font-bold text-white"
						>
							Choose your place <ArrowRight className="h-4 w-4" />
						</Link>
						<p className="mt-4 text-sm">
							$2 per place, per year. Annual renewal keeps the sponsorship
							active.
						</p>
					</div>
					<aside className="rotate-1 rounded-3xl bg-white p-8 shadow-xl shadow-[#243e35]/10">
						<div
							className="flex h-52 items-center justify-center rounded-2xl bg-[#e9eedf]"
							aria-hidden="true"
						>
							<MapPin className="h-20 w-20 text-[#51775f]" strokeWidth={1} />
						</div>
						<p className="mt-7 text-xs font-bold uppercase tracking-widest">
							A little inspiration
						</p>
						<h2 className="mt-3 text-3xl font-black">
							{anniversary
								? 'Another year. Our favourite place.'
								: 'Where our story started.'}
						</h2>
						<p className="mt-4 leading-relaxed">
							{anniversary
								? '“For Sam — the beach we return to every summer. Here’s to all the adventures still to come.”'
								: '“For Alex — that first coffee turned into a whole life of adventures. I’d meet you here all over again.”'}
						</p>
						<p className="mt-6 text-xs text-[#52695f]">
							Illustrative message idea, not a preview of the recipient page.
						</p>
					</aside>
				</section>
				<section className="border-t border-[#243e35]/20 py-12">
					<h2 className="text-3xl font-black">
						A place, a message, a greener future.
					</h2>
					<div className="mt-8 grid gap-8 md:grid-cols-3">
						{[
							[
								'01 / Choose a place',
								'Open the map, search for your location and zoom in to select an available place. The place is a digital keepsake, not physical land.',
							],
							[
								'02 / Make it personal',
								'Choose gifting at checkout and add the recipient’s details and a message. After purchase, customise the place and schedule the gift email.',
							],
							[
								'03 / Share your gift',
								'Your recipient receives an email with a link to claim their place. Each purchase and renewal funds two trees through donations to Trees for the Future.',
							],
						].map(([title, body]) => (
							<div key={title}>
								<h3 className="font-bold">{title}</h3>
								<p className="mt-3 leading-relaxed">{body}</p>
							</div>
						))}
					</div>
				</section>
				<section className="grid gap-8 rounded-3xl bg-[#e9eedf] p-8 md:grid-cols-2 md:p-12">
					<div>
						<TreeDeciduous className="mb-4 h-8 w-8" />
						<h2 className="text-2xl font-black">What your gift includes</h2>
						<p className="mt-4 leading-relaxed">
							A digital place on Erda’s map, a personal message and funding for
							tree planting. No physical print is shipped. The trees are not
							necessarily planted at your chosen map location.
						</p>
					</div>
					<div>
						<h3 className="font-bold">How does renewal work?</h3>
						<p className="mt-3 leading-relaxed">
							The gift costs $2 per place per year. The sender pays for renewal
							to keep the sponsorship active. If the sender stops renewing, the
							recipient is offered the opportunity to renew. This is not a
							lifetime purchase.
						</p>
						<Link
							to="/digital-gift"
							className="mt-5 inline-block font-bold underline"
						>
							Read the full gifting guide
						</Link>
					</div>
				</section>
				<section className="py-12">
					<h2 className="text-2xl font-black">
						{anniversary
							? 'Which place should you choose?'
							: 'What if you met online?'}
					</h2>
					<p className="mt-4 max-w-3xl leading-relaxed">
						{anniversary
							? 'Pick somewhere your partner will recognise: your first home, your wedding venue or a favourite walk. Include a specific memory in your message so the place feels unmistakably yours.'
							: 'Choose where you first met in person, the city you were in when you connected or the place you hope to visit together. If the exact place is taken, choose an available one nearby and explain the connection in your message.'}
					</p>
					<Link
						to={anniversary ? '/gifts/where-we-met' : '/gifts/anniversary'}
						className="mt-6 inline-block font-bold underline"
					>
						{anniversary
							? 'Explore a “where we met” gift'
							: 'Explore digital anniversary gifts'}
					</Link>
				</section>
				<footer className="flex flex-wrap gap-6 border-t border-[#243e35]/20 pt-8 text-sm">
					<Link to="/?map=1&select=paid">Explore the map</Link>
					<Link to="/tree-planting">How tree planting works</Link>
					<Link to="/contact">Contact</Link>
					<Link to="/terms">Terms</Link>
					<Link to="/privacy">Privacy</Link>
				</footer>
			</main>
		</div>
	)
}
